import React from 'react';
import T from 'prop-types';
import { colors } from '@acast/decibel';
import { GlobeLightGlyph, LockLightGlyph, UserLightGlyph } from '@acast/decibel-glyphs';
import AudioPreview from './audio-preview';
import AudioUpload from './audio-upload';
import Card from './card';
import CardIcon from './card-icon';

const PRIVATE_INTRO = 'privateIntro';
const PRIVATE_OUTRO = 'privateOutro';
const PUBLIC_INTRO = 'publicIntro';
const PUBLIC_OUTRO = 'publicOutro';

const AudioCard = ({ episodeId, disabled, onChangeAudioFile, showId, src, type }) => {
  const title = React.useMemo(() => {
    return {
      [PRIVATE_INTRO]: 'Private promotion opening',
      [PRIVATE_OUTRO]: 'Exclusive content',
      [PUBLIC_INTRO]: 'Public promotion opening',
      [PUBLIC_OUTRO]: 'Public ending',
    }[type];
  }, [type]);

  const body = React.useMemo(() => {
    return {
      [PRIVATE_INTRO]:
        'The first thing a listener will hear on the exclusive episode. Use the private opening to thank listeners for their support.',
      [PRIVATE_OUTRO]:
        'The exclusive content to be accessed by paying subscribers. This will appear as the ending of the private episode after the publicly available content.',
      [PUBLIC_INTRO]:
        "The first thing a listener will hear on the public episode. Use the opening to promote the show's Access content.",
      [PUBLIC_OUTRO]:
        "The final thing a listener will hear on the public episode. Use the ending to promote the show's Access content.",
    }[type];
  }, [type]);

  const media = React.useMemo(() => {
    switch (type) {
      case PRIVATE_INTRO:
        return (
          <CardIcon
            background={colors.yellow16}
            foreground={colors.yellowDarken8}
            glyph={LockLightGlyph}
          />
        );

      case PRIVATE_OUTRO:
        return (
          <CardIcon background={colors.blue16} foreground={colors.blue} glyph={UserLightGlyph} />
        );

      case PUBLIC_INTRO:
      case PUBLIC_OUTRO:
        return (
          <CardIcon background={colors.green16} foreground={colors.green} glyph={GlobeLightGlyph} />
        );

      default:
        return undefined;
    }
  }, [type]);

  return (
    <Card disabled={disabled}>
      <Card.Header title={title} media={media} />
      <Card.Content>{body}</Card.Content>
      <Card.Footer
        action={
          <AudioUpload
            disabled={disabled}
            episodeId={episodeId}
            showId={showId}
            hasAudio={!!src}
            onChange={onChangeAudioFile}
          />
        }
      >
        <AudioPreview src={src} />
      </Card.Footer>
    </Card>
  );
};

AudioCard.propTypes = {
  disabled: T.bool,
  episodeId: T.string.isRequired,
  onChangeAudioFile: T.func.isRequired,
  showId: T.string.isRequired,
  src: T.string,
  type: T.oneOf([PRIVATE_INTRO, PRIVATE_OUTRO, PUBLIC_INTRO, PUBLIC_OUTRO]),
};

AudioCard.defaultProps = {
  disabled: false,
  src: undefined,
};

export default AudioCard;
